import { forwardRef, PropsWithoutRef } from "react"
import { useField } from "react-final-form"
import TextField, { TextFieldProps } from "@mui/material/TextField"
import { omit } from "lodash"

export interface LabeledTextFieldProps extends PropsWithoutRef<TextFieldProps> {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  /** Field type. Doesn't include radio buttons and checkboxes */
  type?: "text" | "password" | "email" | "number"
  optional?: boolean
  adornment?: React.ReactNode
  outerProps?: PropsWithoutRef<JSX.IntrinsicElements["div"]>
}

export const LabeledTextField = forwardRef<HTMLInputElement, LabeledTextFieldProps>(
  ({ name, label, outerProps, adornment, ...props }, ref) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
    } = useField(name, {
      parse: (value, name) => {
        if (props.type === "number") {
          return Number(value)
        }

        // Patch for optional string, because react-final-form doesn't add keys for empty strings, so it is impossible to delete them if you have entered them
        if (props.optional && value === "") {
          return null
        }

        return value
      },
    })

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError

    return (
      <div {...outerProps}>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          label={label}
          InputProps={{ endAdornment: adornment }}
          {...input}
          disabled={submitting}
          {...omit(props, "optional")}
          type="text"
          ref={ref}
          error={touched && normalizedError ? true : false}
          helperText={touched && normalizedError && normalizedError}
        />
      </div>
    )
  }
)

export default LabeledTextField
